import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  closestCenter,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ReactComponent as AddIcon } from '@assets/svg/add.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import Button from '@components/uikit/Button/Button';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { filterLocationTreeBySearchValue } from '@helpers/filterLocationTreeBySearchValue';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import {
  findItemDeep,
  flattenTree,
  getChildrenCount,
  getNewDepth,
  getParentId,
  getUpdateTreeData,
} from '../../helpers/dndHelpers';
import { getFacilityByGroup } from '../../helpers/getFacilityByGroup';
import type { IActiveItem, ICurrentData, IFlattenedItem } from '../../model/location';
import { useUpdateTreeMutation } from '../../services/useUpdateTreeMutation';
import { selectParentLocationId } from '../../store/useLocationBlockStore';
import ContextMenu from '../ContextMenu/ContextMenu';
import SortableTreeItem from '../SortableTreeItem/SortableTreeItem';
import style from './style.module.scss';

const indentationWidth = 30;

interface IProps {
  searchValue: string;
}

const LocationBlock: React.FC<IProps> = ({ searchValue }) => {
  const stateRef = useRef<{
    visibleParentIds: { [id: number]: boolean };
    allLocationsState: IFlattenedItem[];
  }>({
    visibleParentIds: {},
    allLocationsState: [],
  });
  const [dndState, setDndState] = useState<IFlattenedItem[]>([]);
  const [activeItem, setActiveItem] = useState<IActiveItem | null>(null);

  const locationSelectValue = selectAppLocationSelectValue();
  const openedParentId = selectParentLocationId();

  const { t } = useTranslation();

  const { data: assetTreeData } = useAssetTreeQuery();
  const { data: profileData } = useUserProfileQuery();
  const { setActiveModal } = useModalActions();

  const { mutate: updateTree } = useUpdateTreeMutation();

  useEffect(() => {
    if (assetTreeData) {
      let selectedLocation = assetTreeData.facilities;

      if (locationSelectValue.facility_id) {
        selectedLocation = [findItemDeep(assetTreeData.facilities, locationSelectValue.filterIds[0])!];
      } else if (locationSelectValue.level_name === 'group') {
        selectedLocation = getFacilityByGroup(assetTreeData.facilities, locationSelectValue.filterIds);
      }

      const filteredTreeItems = searchValue
        ? filterLocationTreeBySearchValue(selectedLocation, searchValue)
        : selectedLocation;
      if (openedParentId)
        stateRef.current.visibleParentIds = {
          ...stateRef.current.visibleParentIds,
          [openedParentId]: true,
        };
      const flattened = flattenTree(filteredTreeItems, stateRef.current.visibleParentIds);
      const visibleDndItems = flattened.filter((item) => !item.hidden);
      stateRef.current.allLocationsState = flattened;

      setDndState(visibleDndItems);
    }
  }, [assetTreeData, locationSelectValue, searchValue]);

  const sortedIds = useMemo(() => {
    return dndState.map(({ id }) => id);
  }, [dndState]);

  const handleDragStart = ({ active }: DragStartEvent) => {
    const item = active.data.current as ICurrentData;
    const { id, depth, index } = item;
    const { allLocationsState, visibleParentIds } = stateRef.current;

    const locStateIndex = allLocationsState.findIndex((item) => item.id === id);
    const childrenCount = getChildrenCount(allLocationsState, locStateIndex, depth);

    if (visibleParentIds[id] && childrenCount > 0) {
      const dndChildrenCount = getChildrenCount(dndState, index, depth);
      const newDndState = [...dndState];
      newDndState.splice(index + 1, dndChildrenCount);

      setDndState(newDndState);
    }

    setActiveItem({ ...item, locStateIndex, childrenCount, newDepth: item.depth });
  };

  const handleDragMove = ({ delta, active, over }: DragMoveEvent) => {
    const { index: activeIndex } = active.data.current as ICurrentData;
    const { id: currentId, depth: currentDepth, newDepth: activeNewDepth } = activeItem!;
    if (currentId === over?.id) {
      if (currentDepth !== activeNewDepth) {
        setActiveItem((item) => ({ ...item, newDepth: currentDepth }) as IActiveItem);
      }
      return;
    }
    const { index: overIndex } = over?.data.current as ICurrentData;

    const dragDepth = Math.trunc(delta.x / indentationWidth);
    const calculatedDepth = currentDepth + dragDepth;

    const indexDelta = activeIndex > overIndex ? overIndex : overIndex + 1;
    const newDepth = getNewDepth(dndState, calculatedDepth, indexDelta);

    if (newDepth !== activeNewDepth) {
      setActiveItem((item) => ({ ...item, newDepth }) as IActiveItem);
    }
  };

  const resetState = () => {
    const { allLocationsState } = stateRef.current;
    setDndState(allLocationsState.filter((item) => !item.hidden));
    setActiveItem(null);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const { id: currentId, locStateIndex, childrenCount, depth, newDepth, facilityId } = activeItem!;
    if (currentId === over?.id) {
      resetState();
      return;
    }

    if (depth === 0 && newDepth !== 0) {
      showToast({ variant: 'error', message: t('Cannot move facility from root') });
      resetState();
      return;
    }

    if (depth !== 0 && newDepth === 0) {
      showToast({ variant: 'error', message: t('Cannot move from current facility') });
      resetState();
      return;
    }

    const { allLocationsState } = stateRef.current;
    const { index: activeIndex } = active.data.current as ICurrentData;
    const { index: overIndex } = over?.data.current as ICurrentData;

    const indexDelta = activeIndex > overIndex ? overIndex : overIndex + 1;
    const dndLeftEl = dndState[indexDelta - 1];
    const dndRightEl = dndState[indexDelta];

    if (depth !== 0 && facilityId !== dndLeftEl?.facilityId) {
      showToast({ variant: 'error', message: t('Cannot move from current facility') });
      resetState();
      return;
    }

    const updatedLocations = [...allLocationsState];
    const activeItems = updatedLocations.splice(locStateIndex, childrenCount + 1);
    const locLeftElIndex = dndLeftEl ? updatedLocations.findIndex((i) => i.id === dndLeftEl?.id) : 0;

    let indexTo = null;

    if (activeIndex > overIndex) {
      // move up
      indexTo = dndLeftEl ? locLeftElIndex + 1 : locLeftElIndex;
    } else {
      // move down
      if (dndLeftEl?.children && dndRightEl?.parentId === dndLeftEl.id) {
        // opened collapse
        indexTo = locLeftElIndex + 1;
      } else {
        // closed collapse
        const locLeftElChildrenCount = getChildrenCount(
          updatedLocations,
          locLeftElIndex,
          updatedLocations[locLeftElIndex].depth,
        );

        indexTo = locLeftElChildrenCount ? locLeftElIndex + locLeftElChildrenCount + 1 : locLeftElIndex + 1;
      }
    }

    const depthDiff = newDepth - depth;
    const newParentId = getParentId(newDepth, dndLeftEl, updatedLocations, indexTo);

    const movedItems = activeItems.map(
      (item, i): IFlattenedItem => ({
        ...item,
        depth: i == 0 ? newDepth : item.depth + depthDiff,
        parentId: i === 0 ? newParentId : item.parentId,
      }),
    );
    updatedLocations.splice(indexTo, 0, ...movedItems);

    const data = getUpdateTreeData(updatedLocations, activeItem!, newParentId);
    updateTree(data, {
      onError: () => {
        showToast({ variant: 'error', message: t('Something went wrong!') });
        resetState();
      },
    });
    setDndState(updatedLocations.filter((item) => !item.hidden));
    stateRef.current.allLocationsState = updatedLocations;
    setActiveItem(null);
  };

  const handleCollapse = (id: number) => {
    const { allLocationsState, visibleParentIds } = stateRef.current;
    const locIndex = allLocationsState.findIndex((item) => item.id === id);
    const locElDepth = allLocationsState[locIndex].depth;

    if (!visibleParentIds[id]) {
      visibleParentIds[id] = true;

      for (let i = locIndex + 1; i < allLocationsState.length; i++) {
        if (allLocationsState[i].depth <= locElDepth) break;
        allLocationsState[i].hidden = visibleParentIds[allLocationsState[i].parentId!] ? false : true;
      }
    } else {
      visibleParentIds[id] = false;

      for (let i = locIndex + 1; i < allLocationsState.length; i++) {
        if (allLocationsState[i].depth <= locElDepth) break;
        allLocationsState[i].hidden = true;
      }
    }

    setDndState(allLocationsState.filter((item) => !item.hidden));
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const sortable = !!profileData?.user?.all_facilities && !searchValue;

  return (
    <div className={style.locationsBlock}>
      <div className={style.locationsBlockHead}>
        <span className={style.locationsBlockTitle}>{t('Locations')}</span>
        {profileData?.userAccess?.manageEquipmentDeactivation && (
          <Button
            type="secondary"
            text={t('Create Location')}
            leftElement={<AddIcon className={style.addIcon} />}
            onClick={() => setActiveModal({ name: MODAL_NAMES.createLocationModal })}
          />
        )}
      </div>
      <div className={style.locationsTree}>
        {dndState.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
              {dndState.map((loc, index) => (
                <SortableTreeItem
                  key={loc.id}
                  item={loc}
                  index={index}
                  depth={loc.id === activeItem?.id ? activeItem.newDepth : loc.depth}
                  indentationWidth={indentationWidth}
                  sortable={sortable}
                  collapsed={!stateRef.current.visibleParentIds[loc.id]}
                  onCollapse={loc.children ? () => handleCollapse(loc.id) : undefined}
                />
              ))}
              {createPortal(
                <DragOverlay
                  dropAnimation={defaultDropAnimation}
                  modifiers={[
                    ({ transform }) => ({
                      ...transform,
                      y: transform.y - 25,
                    }),
                  ]}
                >
                  {activeItem ? (
                    <SortableTreeItem
                      clone
                      item={activeItem}
                      depth={activeItem.depth}
                      childrenCount={activeItem.childrenCount}
                      indentationWidth={indentationWidth}
                      sortable={sortable}
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )}
            </SortableContext>
          </DndContext>
        ) : (
          <table className={style.tableBodyRowEmpty}>
            <tbody>
              <TableEmptyRowExtended text={t('No Locations Were Found.')} />
            </tbody>
          </table>
        )}
        <ContextMenu id="location-action-menu" />
      </div>
    </div>
  );
};

export default LocationBlock;
